import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../Security/firebase-config";
import { useAuth } from "../../context/AuthContext";
import { FUNCTION_NAMES } from "../../utils/functionNames";
import { buildTeamScopedPath } from "../../utils/teamRouteTarget";

import "./PayerActionOverlay.css";

const HIDDEN_STORAGE_KEY = "payer-action-overlay:hidden-alerts";
const SESSION_DISMISS_STORAGE_KEY = "payer-action-overlay:dismissed-for-session";
const REFRESH_INTERVAL_MS = 45000;
const HIDE_ROUTES = new Set([
  "/",
  "/login",
  "/signup",
  "/verify",
  "/verify-email",
  "/admin/strats-planner",
]);

function readHiddenAlertIds() {
  try {
    const raw = sessionStorage.getItem(HIDDEN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : [];
  } catch (error) {
    console.warn("PayerActionOverlay: failed to read hidden alert ids", error);
    return [];
  }
}

function writeHiddenAlertIds(ids) {
  try {
    sessionStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    console.warn("PayerActionOverlay: failed to persist hidden alert ids", error);
  }
}

function readSessionDismissed() {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_STORAGE_KEY) === "1";
  } catch (error) {
    console.warn("PayerActionOverlay: failed to read session dismissal state", error);
    return false;
  }
}

function writeSessionDismissed(value) {
  try {
    if (value) {
      sessionStorage.setItem(SESSION_DISMISS_STORAGE_KEY, "1");
      return;
    }

    sessionStorage.removeItem(SESSION_DISMISS_STORAGE_KEY);
  } catch (error) {
    console.warn("PayerActionOverlay: failed to persist session dismissal state", error);
  }
}

function formatTeamDeadline(deadlineAt, teamTimeZone) {
  if (typeof deadlineAt !== "number" || !Number.isFinite(deadlineAt)) {
    return "the next billing deadline";
  }

  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: teamTimeZone || "UTC",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).formatToParts(new Date(deadlineAt * 1000));

    const timeZonePart = parts.find((part) => part.type === "timeZoneName")?.value || "UTC";
    const withoutTimeZone = parts
      .filter((part) => part.type !== "timeZoneName")
      .map((part) => part.value)
      .join("")
      .trim()
      .replace(/\s+,/g, ",");

    return `${withoutTimeZone} (Timezone: ${timeZonePart})`;
  } catch (error) {
    console.warn("PayerActionOverlay: invalid team time zone", {
      teamTimeZone,
      error,
    });

    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(deadlineAt * 1000));
  }
}

function buildRequirementLines(alert) {
  const lines = [];

  if (alert?.needsApproval) {
    lines.push("Approve the payment split.");
  }

  if (alert?.needsPaymentMethod) {
    lines.push("Complete your payment setup.");
  }

  return lines;
}

function buildTimingCopy(alert) {
  if (alert?.alertType === "next_cycle_transition_blocker") {
    return `This needs to be completed before ${formatTeamDeadline(
      alert?.deadlineAt,
      alert?.teamTimeZone
    )} for the subscription plan to transition smoothly.`;
  }

  return "This needs to be completed before the team subscription can start.";
}

const PayerActionOverlay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();

  const [alerts, setAlerts] = useState([]);
  const [hiddenAlertIds, setHiddenAlertIds] = useState(() => readHiddenAlertIds());
  const [dismissedForSession, setDismissedForSession] = useState(() => readSessionDismissed());
  const [loading, setLoading] = useState(false);

  const loadAlerts = useCallback(async () => {
    if (!userData?.uid || !FUNCTION_NAMES.getMyPayerActionAlerts) {
      setAlerts([]);
      return;
    }

    try {
      setLoading(true);
      const getMyPayerActionAlerts = httpsCallable(
        functions,
        FUNCTION_NAMES.getMyPayerActionAlerts
      );
      const result = await getMyPayerActionAlerts({});
      const nextAlerts = Array.isArray(result?.data?.alerts) ? result.data.alerts : [];
      setAlerts(nextAlerts);
    } catch (error) {
      console.error("PayerActionOverlay: failed to load alerts", error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [userData?.uid]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    if (!userData?.uid) return undefined;

    const intervalId = window.setInterval(loadAlerts, REFRESH_INTERVAL_MS);

    const handleFocus = () => loadAlerts();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadAlerts();
      }
    };
    const handleRefreshRequest = () => loadAlerts();

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("payer-action-overlay:refresh", handleRefreshRequest);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("payer-action-overlay:refresh", handleRefreshRequest);
    };
  }, [loadAlerts, userData?.uid]);

  useEffect(() => {
    const visibleIds = new Set(alerts.map((alert) => alert?.alertId).filter(Boolean));
    const nextHiddenIds = hiddenAlertIds.filter((alertId) => visibleIds.has(alertId));

    if (nextHiddenIds.length !== hiddenAlertIds.length) {
      setHiddenAlertIds(nextHiddenIds);
      writeHiddenAlertIds(nextHiddenIds);
    }
  }, [alerts, hiddenAlertIds]);

  const visibleAlerts = useMemo(() => {
    const hiddenIds = new Set(hiddenAlertIds);
    return alerts.filter((alert) => !hiddenIds.has(alert?.alertId));
  }, [alerts, hiddenAlertIds]);

  const activeAlert = visibleAlerts[0] || null;
  const additionalAlerts = visibleAlerts.slice(1);
  const requirementLines = activeAlert ? buildRequirementLines(activeAlert) : [];
  const shouldHideForRoute =
    HIDE_ROUTES.has(location.pathname) || location.pathname.startsWith("/pick-username");

  const dismissAlert = useCallback((alertId) => {
    writeSessionDismissed(true);
    setDismissedForSession(true);

    if (!alertId) return;

    setHiddenAlertIds((current) => {
      if (current.includes(alertId)) return current;
      const next = [...current, alertId];
      writeHiddenAlertIds(next);
      return next;
    });
  }, []);

  const handleCompleteNow = useCallback(async () => {
    if (!activeAlert?.teamId) return;

    navigate(buildTeamScopedPath("/team-setup-payers", activeAlert.teamId));
  }, [activeAlert?.teamId, navigate]);

  if (!userData?.uid || shouldHideForRoute || dismissedForSession || !activeAlert) {
    return null;
  }

  return (
    <aside
      className="PayerActionOverlay"
      aria-live="polite"
      aria-busy={loading ? "true" : "false"}
    >
      <button
        type="button"
        className="PayerActionOverlay__close app-cancel-control"
        onClick={() => dismissAlert(activeAlert.alertId)}
        aria-label="Hide payer action reminder for this session"
      >
        x
      </button>

      <div className="PayerActionOverlay__eyebrow">Payment Action Needed</div>
      <div className="PayerActionOverlay__team">{activeAlert.teamName}</div>

      <p className="PayerActionOverlay__copy">
        You were selected as a payer for this team.
      </p>

      <div className="PayerActionOverlay__requirements">
        {requirementLines.map((line) => (
          <div key={line} className="PayerActionOverlay__requirement">
            {line}
          </div>
        ))}
      </div>

      <p className="PayerActionOverlay__timing">{buildTimingCopy(activeAlert)}</p>

      {additionalAlerts.length > 0 ? (
        <div className="PayerActionOverlay__more">
          {additionalAlerts.length} more team
          {additionalAlerts.length === 1 ? "" : "s"} also require action.
        </div>
      ) : null}

      <div className="PayerActionOverlay__actions">
        <button
          type="button"
          className="PayerActionOverlay__button PayerActionOverlay__button--primary"
          onClick={handleCompleteNow}
        >
          Complete Now
        </button>
      </div>
    </aside>
  );
};

export default PayerActionOverlay;
